import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import PublicLayout from "../components/layout/PublicLayout";
import { api, formatMoney, formatApiError, isKycRequiredError } from "../lib/api";
import PriceDisplay from "../components/PriceDisplay";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { carImageUrl } from "../components/cars/CarCard";
import KycRequiredDialog from "../components/KycRequiredDialog";
import { toast } from "sonner";
import { Star, Users, Fuel, Cog, MapPin, ShieldCheck, ArrowRight, Info, MessageCircle, ShieldPlus, ExternalLink } from "lucide-react";
import { LocationDisplay } from "../components/LocationPicker";
import { DatePicker } from "../components/DatePicker";
import NegotiateDialog from "../components/NegotiateDialog";

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m of [0, 30]) {
    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    const val = `${hh}:${mm}`;
    const ampm = h < 12 ? "AM" : "PM";
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    TIME_OPTIONS.push({ val, label: `${displayH}:${mm} ${ampm}` });
  }
}
function todayPlus(d) {
  const dt = new Date();
  dt.setDate(dt.getDate() + d);
  return dt.toISOString().split("T")[0];
}

export default function CarDetail() {
  const { id } = useParams();
  const [unavailableRanges, setUnavailableRanges] = useState([]);
  useEffect(() => {
    if (!id) return;
    api.get(`/cars/${id}/availability`).then(({ data }) => {
      const ranges = [
        ...(data.blocks || []).map((b) => ({ start_date: b.start_date, end_date: b.end_date })),
        ...(data.bookings || []).map((b) => ({ start_date: b.start_date, end_date: b.end_date })),
      ];
      setUnavailableRanges(ranges);

      // If the user landed directly on this car (no explicit dates from search), and the
      // default date range collides with a booked/blocked range, auto-advance to the next
      // available window of the same length — mirrors Turo's behavior.
      const hasExplicitDates = searchParams.get('from') || searchParams.get('until');
      if (hasExplicitDates || ranges.length === 0) return;

      const rangeLengthDays = Math.max(1, Math.round(
        (new Date(`${dates.end}T00:00:00`) - new Date(`${dates.start}T00:00:00`)) / 86400000
      ));

      const overlaps = (start, end) => ranges.some((r) => {
        const rs = new Date(`${r.start_date}T00:00:00`);
        const re = new Date(`${r.end_date}T00:00:00`);
        return start < re && end > rs;
      });

      let cursorStart = new Date(`${dates.start}T00:00:00`);
      let cursorEnd = new Date(cursorStart);
      cursorEnd.setDate(cursorEnd.getDate() + rangeLengthDays);

      const maxLookaheadDays = 180;
      let daysChecked = 0;
      while (overlaps(cursorStart, cursorEnd) && daysChecked < maxLookaheadDays) {
        cursorStart.setDate(cursorStart.getDate() + 1);
        cursorEnd = new Date(cursorStart);
        cursorEnd.setDate(cursorEnd.getDate() + rangeLengthDays);
        daysChecked += 1;
      }

      const toLocalISODate = (d) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };

      if (daysChecked > 0 && daysChecked < maxLookaheadDays) {
        setDates({
          start: toLocalISODate(cursorStart),
          end: toLocalISODate(cursorEnd),
        });
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { currencies, selectedCurrency } = useCurrency();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [dates, setDates] = useState(() => {
    const fromParam = searchParams.get('from');
    const untilParam = searchParams.get('until');
    const start = fromParam ? fromParam.split('T')[0] : "";
    const end = untilParam ? untilParam.split('T')[0] : "";
    return { start, end };
  });
  const [times, setTimes] = useState(() => {
    const fromParam = searchParams.get('from');
    const untilParam = searchParams.get('until');
    const start = fromParam?.split('T')[1] || "";
    const end = untilParam?.split('T')[1] || "";
    return { start, end };
  });
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  // Lock the return time to the owner's fixed return deadline, if one is set.
  useEffect(() => {
    if (car?.fixed_return_time) {
      setTimes((t) => (t.end === car.fixed_return_time ? t : { ...t, end: car.fixed_return_time }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car?.fixed_return_time]);
  const [booking, setBooking] = useState(false);
  const [kycDialog, setKycDialog] = useState(false);
  const [kycStatusForDialog, setKycStatusForDialog] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [showNegotiate, setShowNegotiate] = useState(false);
  const [withDriver, setWithDriver] = useState(false);

  useEffect(() => {
    api.get(`/cars/${id}`)
      .then(({ data }) => setCar(data))
      .catch(() => toast.error("Car not found"))
      .finally(() => setLoading(false));
    api.get(`/cars/${id}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
  }, [id]);

  const fetchQuote = async () => {
    if (!car || !dates.start || !dates.end) return;
    setQuoting(true);
    try {
      const { data } = await api.post("/bookings/quote", {
        car_id: car.id,
        start_date: dates.start,
        end_date: dates.end,
        start_time: times.start,
        end_time: times.end,
        display_currency: selectedCurrency,
        with_driver: withDriver,
      });
      setQuote(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setQuoting(false);
    }
  };

  useEffect(() => {
    fetchQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car?.id, dates.start, dates.end, times.start, times.end, selectedCurrency, withDriver]);

  const isDatesAvailable = () => {
    if (!unavailableRanges.length) return true;
    const start = new Date(`${dates.start}T00:00:00`);
    const end = new Date(`${dates.end}T00:00:00`);
    return !unavailableRanges.some(r => {
      const rs = new Date(`${r.start_date}T00:00:00`);
      const re = new Date(`${r.end_date}T00:00:00`);
      return start < re && end > rs;
    });
  };

  const handleBook = async () => {
    if (!isDatesAvailable()) {
      toast.error("These dates are already booked. Please select different dates.");
      return;
    }
    if (!user) {
      // Guest checkout — redirect to guest checkout with car + dates
      nav(`/guest/checkout?car_id=${car.id}&start_date=${dates.start}&end_date=${dates.end}&start_time=${times.start}&end_time=${times.end}${withDriver ? "&with_driver=1" : ""}`);
      return;
    }
    if (user.role === "admin") {
      toast.error("Admins cannot book cars");
      return;
    }
    setBooking(true);
    try {
      const { data } = await api.post("/bookings", {
        car_id: car.id,
        start_date: dates.start,
        end_date: dates.end,
        start_time: times.start,
        end_time: times.end,
        display_currency: selectedCurrency,
        with_driver: withDriver,
      });
      nav(`/checkout/${data.id}`);
    } catch (e) {
      if (isKycRequiredError(e)) {
        setKycStatusForDialog(e.response?.data?.detail?.kyc_status || "pending");
        setKycDialog(true);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBooking(false);
    }
  };

  if (loading) return <PublicLayout><div className="max-w-7xl mx-auto py-20 text-center text-muted-foreground">Loading…</div></PublicLayout>;
  if (!car) return <PublicLayout><div className="max-w-7xl mx-auto py-20 text-center">Car not found.</div></PublicLayout>;

  const images = car.images?.length ? car.images : [null];

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Image gallery */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
          <div className="md:col-span-8 rounded-lg overflow-hidden border border-border bg-muted aspect-[16/10]" data-testid="car-hero-image">
            <img src={carImageUrl(images[activeImg])} alt={car.title} className="w-full h-full object-cover" />
          </div>
          <div className="md:col-span-4 grid grid-cols-3 md:grid-cols-2 md:grid-rows-2 gap-3 md:gap-4">
            {images.slice(0, 4).map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                data-testid={`car-thumb-${i}`}
                className={`rounded-lg overflow-hidden border ${activeImg === i ? "border-primary ring-2 ring-primary/30" : "border-border"} aspect-square md:aspect-auto`}
              >
                <img src={carImageUrl(img)} alt={`${car.title} ${i}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Details + booking widget */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-10">
          <div className="md:col-span-7 space-y-8">
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge variant="outline" className="text-xs">{car.car_type}</Badge>
                <Badge variant="outline" className="text-xs">{car.year}</Badge>
                <Badge variant="outline" className="text-xs capitalize">{car.transmission}</Badge>
                {car.provides_driver && car.driver_mandatory ? (
                  <Badge className="text-xs bg-amber-500 text-white hover:bg-amber-500">Driver Mandatory</Badge>
                ) : car.provides_driver ? (
                  <Badge className="text-xs bg-accent text-accent-foreground hover:bg-accent">Driver Optional</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">No Driver</Badge>
                )}
                {car.sponsored && <Badge className="text-xs bg-[#E97A48] text-white hover:bg-[#E97A48]">Sponsored</Badge>}
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">{car.title}</h1>
              <div className="text-sm text-muted-foreground mt-1.5 flex items-center gap-1.5">
                <MapPin size={13} /> {car.location} · {car.city}, {car.country_code}
              </div>
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { i: Users, l: "Seats", v: car.seats },
                  { i: Cog, l: "Transmission", v: car.transmission },
                  { i: Fuel, l: "Fuel", v: car.fuel },
                  { i: Star, l: "Rating", v: car.rating?.toFixed?.(1) || "New" },
                ].map((x, i) => (
                  <div key={i} className="border border-border rounded-md p-3 flex items-center gap-3">
                    <x.i size={16} className="text-primary" />
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{x.l}</div>
                      <div className="font-display font-semibold text-sm">{x.v}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            <div>
              <h2 className="font-display text-2xl font-semibold tracking-tight">About this car</h2>
              <p className="mt-3 text-foreground/80 leading-relaxed">
                {car.description || `A reliable ${car.make ? car.make + " " : ""}${car.model_name} (${car.year}) ready for your trip. ${car.transmission} transmission, ${car.fuel} fuel, ${car.seats} seats.`}
              </p>
            </div>

            {car.features?.length > 0 && (
              <div>
                <h3 className="font-display text-lg font-semibold tracking-tight">Features</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {car.features.map((f, i) => <Badge key={i} variant="secondary" className="capitalize">{f}</Badge>)}
                </div>
              </div>
            )}

            <Separator />

            {/* Reviews */}
            <div data-testid="car-reviews-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-lg font-semibold tracking-tight">Reviews</h3>
                {car.rating_count > 0 && (
                  <div className="flex items-center gap-1.5 text-sm">
                    <Star size={14} className="fill-accent text-accent" />
                    <span className="font-display font-bold">{car.rating?.toFixed?.(1)}</span>
                    <span className="text-muted-foreground">({car.rating_count})</span>
                  </div>
                )}
              </div>
              {reviews.length === 0 ? (
                <p className="text-sm text-muted-foreground">No reviews yet. Be the first to share your experience.</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((r) => (
                    <div key={r.id} className="border-b border-border last:border-b-0 pb-4 last:pb-0" data-testid={`review-${r.id}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                          {r.customer_name?.charAt(0)?.toUpperCase() || "U"}
                        </div>
                        <div className="flex-1">
                          <div className="font-display font-medium text-sm">{r.customer_name}</div>
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <Star key={n} size={10} className={n <= r.rating ? "fill-accent text-accent" : "text-muted-foreground/30"} />
                            ))}
                            <span className="text-xs text-muted-foreground ml-1.5">{r.created_at?.split("T")[0]}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-foreground/80 leading-relaxed">{r.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Owner */}
            <div>
              <h3 className="font-display text-lg font-semibold tracking-tight">
                Hosted by {car.contact_unlocked ? (car.owner?.name || "Owner") : (car.owner?.name_masked || "Owner")}
              </h3>
              <div className="mt-3 flex items-center gap-4 flex-wrap">
                <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display text-xl font-bold">
                  {(car.contact_unlocked ? car.owner?.name : car.owner?.name_masked)?.charAt(0)?.toUpperCase() || "O"}
                </div>
                <div className="flex-1 min-w-[120px]">
                  <div className="font-display font-semibold">
                    {car.contact_unlocked ? car.owner?.name : car.owner?.name_masked}
                  </div>
                  <div className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <ShieldCheck size={13} className="text-primary" /> Verified owner
                  </div>
                  {!car.contact_unlocked && car.owner?.phone_masked && (
                    <div className="text-xs text-muted-foreground mt-1 font-mono" data-testid="owner-phone-masked">
                      {car.owner.phone_masked}
                    </div>
                  )}
                </div>
                {car.contact_unlocked && car.owner?.phone && car.whatsapp_enabled && (
                  <a
                    href={`https://wa.me/${car.owner.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi ${car.owner.name || ""}, I'm interested in renting your ${car.title} on RydeXpress.`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#25D366] text-white text-sm font-medium hover:bg-[#1da851] transition-colors btn-lift"
                    data-testid="whatsapp-owner-btn"
                  >
                    <MessageCircle size={14} /> Message on WhatsApp
                  </a>
                )}
                {car.contact_unlocked && car.owner?.phone && (
                  <a
                    href={`tel:${car.owner.phone}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#2A9D8F] text-white text-sm font-medium hover:bg-[#23887A] transition-colors"
                    data-testid="call-owner-btn"
                  >
                    📞 {car.owner.phone}
                  </a>
                )}
                {!car.contact_unlocked && (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-muted text-muted-foreground text-sm border border-dashed border-border" data-testid="owner-locked-cta">
                    🔒 Pay booking fee to unlock contact
                  </div>
                )}
              </div>
            </div>

            {(car.location_lat != null && car.location_lng != null) && (
              <>
                <Separator />
                <div>
                  <h3 className="font-display text-lg font-semibold tracking-tight mb-3">Pickup location</h3>
                  <div className="flex items-start justify-between gap-3 p-4 rounded-lg border border-border bg-muted/30">
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                      <span className="text-sm">{car.location} · {car.city}, {car.country_code}</span>
                    </div>
                    
                      <a
                      href={`https://www.google.com/maps/search/?api=1&query=${car.location_lat},${car.location_lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 flex items-center gap-1 text-xs font-semibold text-accent hover:underline whitespace-nowrap"
                      data-testid="open-map-link"
                    >
                      Open in Maps <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Booking widget */}
          <aside className="md:col-span-5">
            <Card className="border-border shadow-organic p-6 md:sticky md:top-24" data-testid="booking-widget">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="font-display text-3xl font-bold tracking-tight"><PriceDisplay amount={car.price_per_day} listingCurrency={car.currency} /></div>
                  <div className="text-xs text-muted-foreground -mt-0.5">per day</div>
                </div>
                <Badge className="bg-accent text-accent-foreground hover:bg-accent">{car.status === "active" ? "Available" : car.status}</Badge>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Pickup</Label>
                  <DatePicker
                    testId="booking-start-input"
                    value={dates.start}
                    min={todayPlus(0)}
                    unavailableRanges={unavailableRanges}
                    onChange={(v) => setDates({ ...dates, start: v })}
                  />
                  <select value={times.start} onChange={(e) => setTimes({ ...times, start: e.target.value })}
                    data-testid="booking-start-time"
                    className="mt-1.5 w-full h-9 rounded-md border border-border bg-background px-2 text-xs">
                    <option value="" disabled>Select time</option>
                    {TIME_OPTIONS.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Return</Label>
                  <DatePicker
                    testId="booking-end-input"
                    value={dates.end}
                    min={dates.start || todayPlus(0)}
                    unavailableRanges={unavailableRanges}
                    onChange={(v) => setDates({ ...dates, end: v })}
                  />
                  <select value={times.end} onChange={(e) => setTimes({ ...times, end: e.target.value })}
                    data-testid="booking-end-time"
                    className="mt-1.5 w-full h-9 rounded-md border border-border bg-background px-2 text-xs">
                    <option value="" disabled>Select time</option>
                    {TIME_OPTIONS.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                  </select>
                  {car?.fixed_return_time && (
                    <div
                      className={`text-[10px] mt-1 ${times.end && times.end > car.fixed_return_time ? "text-amber-700 font-medium" : "text-muted-foreground"}`}
                      data-testid="fixed-return-time-note"
                    >
                      {times.end && times.end > car.fixed_return_time
                        ? `Past the host's ${TIME_OPTIONS.find((t) => t.val === car.fixed_return_time)?.label || car.fixed_return_time} cutoff — 1 extra day added.`
                        : `Host's cutoff: ${TIME_OPTIONS.find((t) => t.val === car.fixed_return_time)?.label || car.fixed_return_time} — returns after this add an extra day.`}
                    </div>
                  )}
                </div>
              </div>

              <Separator className="my-5" />

              {quote ? (
                <div className="space-y-2 text-sm" data-testid="quote-breakdown">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Car Fee: <PriceDisplay amount={quote.price_per_day} listingCurrency={quote.currency} /> × {quote.days} day{quote.days > 1 ? "s" : ""}</span>
                    <span><PriceDisplay amount={quote.price_per_day * quote.days} listingCurrency={quote.currency} /></span>
                  </div>
                  {quote.driver_available && (
                    <div className="flex items-center justify-between">
                      {quote.driver_mandatory ? (
                        <span className="text-muted-foreground">Driver ({quote.days} day{quote.days > 1 ? "s" : ""}, mandatory)</span>
                      ) : (
                        <label className="flex items-center gap-2 text-muted-foreground cursor-pointer">
                          <input type="checkbox" checked={withDriver} onChange={(e) => setWithDriver(e.target.checked)} data-testid="with-driver-checkbox" />
                          Add a driver (<PriceDisplay amount={quote.driver_fee_per_day} listingCurrency={quote.currency} />/day)
                        </label>
                      )}
                      <span>{quote.driver_included ? (quote.driver_fee > 0 ? <PriceDisplay amount={quote.driver_fee} listingCurrency={quote.currency} /> : "Included") : ""}</span>
                    </div>
                  )}
                  <Separator className="my-2" />
                  <div className="flex justify-between font-display text-lg font-bold">
                    <span>Total ({selectedCurrency})</span>
                    <span><PriceDisplay amount={quote.total} listingCurrency={quote.currency} /></span>
                  </div>
                  <div className="flex justify-between items-center rounded-md px-3 py-2.5 bg-emerald-50/60 border border-emerald-100">
                    <div>
                      <div className="font-semibold text-sm text-foreground">Due Today ({(quote.commission_rate * 100).toFixed(0)}% Booking Fee)</div>
                      <div className="text-xs text-muted-foreground">Non-refundable</div>
                    </div>
                    <div className="font-display font-bold text-lg text-emerald-700"><PriceDisplay amount={quote.commission} listingCurrency={quote.currency} /></div>
                  </div>
                  <div className="mt-3 p-3 rounded-md bg-emerald-50 border border-emerald-200">
                    <div className="flex items-start gap-2 text-xs text-emerald-800">
                      <ShieldCheck size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                      <div>
                        Pay only <strong>{(quote.commission_rate * 100).toFixed(0)}%</strong> (<PriceDisplay amount={quote.commission} listingCurrency={quote.currency} />) today to book this vehicle.
                        The remaining <strong>{(100 - quote.commission_rate * 100).toFixed(0)}%</strong> (<PriceDisplay amount={quote.owner_net} listingCurrency={quote.currency} />) is paid directly to the host when you pick up the car.
                      </div>
                    </div>
                  </div>

                </div>
              ) : (
                <div className="text-sm text-muted-foreground">{quoting ? "Calculating…" : "Select dates to see the breakdown"}</div>
              )}

              <Button
                onClick={handleBook}
                disabled={booking || !quote || !isDatesAvailable() || !times.start || !times.end}
                className="mt-6 w-full h-11 bg-accent hover:bg-accent/90 text-accent-foreground btn-lift"
                data-testid="book-now-btn"
              >
                {booking ? "Creating booking…" : "Continue to checkout"} <ArrowRight size={16} className="ml-1.5" />
              </Button>
              {(!user || (user.role !== "admin" && car?.owner_id !== user.id)) && (
                <Button
                  variant="outline"
                  onClick={() => setShowNegotiate(true)}
                  disabled={!isDatesAvailable() || !dates.start || !dates.end || !times.start || !times.end}
                  className="mt-2 w-full h-11 border-[#E97A48] text-[#E97A48] hover:bg-[#FCE9DC] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="propose-price-btn"
                >
                  💰 Negotiate the Price
                </Button>
              )}
              {!isDatesAvailable() && (
                <div className="text-xs text-destructive text-center mt-2">Selected dates are not available for this car.</div>
              )}
            </Card>
          </aside>
        </div>
        <div className="h-20" />
      </div>
      <KycRequiredDialog
        onContinueAnyway={async () => {
          setKycDialog(false);
          setBooking(true);
          try {
            const { data } = await api.post("/bookings", {
              car_id: car.id,
              start_date: dates.start,
              end_date: dates.end,
              start_time: times.start,
              end_time: times.end,
              display_currency: selectedCurrency,
            });
            nav(`/checkout/${data.id}`);
          } catch (e) {
            toast.error(formatApiError(e));
          } finally {
            setBooking(false);
          }
        }}
        open={kycDialog}
        onOpenChange={setKycDialog}
        kycStatus={kycStatusForDialog}
        redirectTo="/account/kyc"
      />
      <NegotiateDialog open={showNegotiate} onOpenChange={setShowNegotiate} car={car} dates={dates} times={times} isGuest={!user} />
    </PublicLayout>
  );
}
